/**
 * @fileoverview Type definitions for Firebase P2P Integration Testing
 *
 * Extends the local test types with Firebase-specific structures for
 * testing game state synchronization between two engine instances.
 */

import { PieceType, Position, GameState, ISquare, State } from "../../zodiacEngine/types";
import { IntegrationTest, TestResult, TestSuite } from "../types";
import { LocalTestResult, TestDelta, TestStep } from "../LocalTestRunner";

/**
 * Configuration for Firebase P2P testing
 */
export interface FirebaseTestConfig {
  /** The test to run */
  test: IntegrationTest;
  /** Simulated network latency in ms (0-500) */
  networkLatency?: number;
  /** Maximum time for entire test in ms */
  timeout?: number;
  /** Polling interval for sync checks in ms */
  syncPollInterval?: number;
}

/**
 * Default configuration values
 */
export const DEFAULT_FIREBASE_TEST_CONFIG: Required<Omit<FirebaseTestConfig, "test">> = {
  networkLatency: 0,
  timeout: 60000,
  syncPollInterval: 50,
};

/**
 * Timeouts for various Firebase operations
 */
export const FIREBASE_TIMEOUTS = {
  WRITE: 5000,
  READ: 10000,
  SYNC_BARRIER: 15000,
  FULL_TEST: 60000,
};

/**
 * Tracking piece ID changes after Firebase sync
 */
export interface PieceIdChange {
  /** The piece type */
  pieceType: PieceType;
  /** Position where the piece was found */
  position: Position;
  /** ID before Firebase sync */
  beforeId: string;
  /** ID after Firebase sync */
  afterId: string;
  /** How the piece was matched after ID change */
  resolvedBy: "exact" | "position" | "type_color" | "failed";
}

/**
 * State consistency check between two engines
 */
export interface ConsistencyCheck {
  /** What field was checked */
  field: "fen" | "turn" | "turnCount" | "bonusTurn" | "activePieceId" | "statusEffects" | "moveHistory";
  /** Value from Light engine */
  lightValue: unknown;
  /** Value from Dark engine */
  darkValue: unknown;
  /** Whether values match */
  matches: boolean;
  /** Details if mismatch */
  discrepancy?: string;
}

/**
 * Timing information for sync operations
 */
export interface SyncTiming {
  /** Name of the sync operation */
  step: string;
  /** Duration in milliseconds */
  durationMs: number;
  /** When the sync started */
  startedAt: number;
}

/**
 * Result of bonus turn synchronization check
 */
export interface BonusTurnSyncResult {
  /** Whether bonus turn state is synchronized */
  synced: boolean;
  /** Light engine's skipTurnChange value */
  lightSkipTurnChange: boolean;
  /** Dark engine's skipTurnChange value */
  darkSkipTurnChange: boolean;
  /** Light engine's activeBonusTurnPieceId */
  lightActivePieceId: string | null;
  /** Dark engine's activeBonusTurnPieceId */
  darkActivePieceId: string | null;
  /** Details if desync detected */
  details?: string;
}

/**
 * Extended test result with Firebase P2P specific information
 */
export interface FirebaseTestResult extends LocalTestResult {
  /** Number of Firebase round-trips during test */
  firebaseRoundTrips: number;
  /** Piece ID changes tracked during test */
  pieceIdChanges: PieceIdChange[];
  /** State consistency checks performed */
  stateConsistencyChecks: ConsistencyCheck[];
  /** Timing of sync operations */
  syncTimings: SyncTiming[];
  /** Bonus turn synchronization results */
  bonusTurnSync?: BonusTurnSyncResult;
  /** Final FEN from Light engine */
  lightEngineFEN: string;
  /** Final FEN from Dark engine */
  darkEngineFEN: string;
  /** Whether Light and Dark engines agree on final state */
  enginesConsistent: boolean;
}

/**
 * Error types specific to Firebase P2P testing
 */
export enum FirebaseTestError {
  SYNC_TIMEOUT = "sync_timeout",
  CONSISTENCY_MISMATCH = "consistency_mismatch",
  PIECE_ID_RESOLUTION_FAILED = "piece_id_resolution_failed",
  BONUS_TURN_DESYNC = "bonus_turn_desync",
  FIREBASE_WRITE_FAILED = "firebase_write_failed",
  FIREBASE_READ_FAILED = "firebase_read_failed",
  ENGINE_INITIALIZATION_FAILED = "engine_initialization_failed",
  MOVE_EXECUTION_FAILED = "move_execution_failed",
  UNEXPECTED_GAME_STATE = "unexpected_game_state",
}

/**
 * Result category for Firebase tests
 */
export type FirebaseTestResultCategory =
  | "PASS"
  | "FAIL_FEN"
  | "FAIL_RESULT"
  | "FAIL_CONSISTENCY"
  | "FAIL_SYNC_TIMEOUT"
  | "FAIL_ID_RESOLUTION"
  | "FAIL_BONUS_TURN_DESYNC"
  | "FAIL_ERROR";

/**
 * Categorize a Firebase test result
 */
export function categorizeResult(result: FirebaseTestResult): FirebaseTestResultCategory {
  if (result.passed && result.enginesConsistent) {
    return "PASS";
  }

  // Check for specific failure modes
  if (result.error?.includes("sync_timeout") || result.error?.includes("Timeout")) {
    return "FAIL_SYNC_TIMEOUT";
  }

  if (!result.enginesConsistent) {
    return "FAIL_CONSISTENCY";
  }

  if (result.pieceIdChanges.some((c) => c.resolvedBy === "failed")) {
    return "FAIL_ID_RESOLUTION";
  }

  if (result.bonusTurnSync && !result.bonusTurnSync.synced) {
    return "FAIL_BONUS_TURN_DESYNC";
  }

  if (!result.fenMatches) {
    return "FAIL_FEN";
  }

  if (!result.resultMatches) {
    return "FAIL_RESULT";
  }

  return "FAIL_ERROR";
}

/**
 * Suite result for Firebase P2P tests
 */
export interface FirebaseTestSuiteResult {
  suiteName: string;
  results: FirebaseTestResult[];
  passed: number;
  failed: number;
  duration: number;
  /** Breakdown by failure category */
  failureBreakdown: Record<FirebaseTestResultCategory, number>;
  /** Total Firebase round-trips across all tests */
  totalRoundTrips: number;
  /** Average sync time in ms */
  averageSyncTime: number;
}

/**
 * State snapshot from an engine for comparison
 */
export interface EngineStateSnapshot {
  fen: string;
  isLightTurn: boolean;
  skipTurnChange: boolean;
  activeBonusTurnPieceId: string | null;
  moveHistoryLength: number;
  gameEnded: boolean;
  winner?: "light" | "dark";
}

/**
 * Extract a snapshot from a GameState for comparison
 */
export function extractStateSnapshot(state: GameState): EngineStateSnapshot {
  // Import ZodiacFEN lazily to avoid circular dependencies
  const { ZodiacFEN } = require("../../zodiacEngine/Fen/ZodiacFEN");

  return {
    fen: ZodiacFEN.encode(state),
    isLightTurn: state.isLightTurn,
    skipTurnChange: state.skipTurnChange ?? false,
    activeBonusTurnPieceId: state.activeBonusTurnPieceId ?? null,
    moveHistoryLength: state.fenHistory?.length ?? 0,
    gameEnded: state.state === State.PostGame || state.state === State.Draw || state.state === State.Terminated,
    winner: state.lightWon === true ? "light" : state.lightWon === false ? "dark" : undefined,
  };
}

/**
 * Test game configuration for dual-engine setup
 */
export interface DualEngineTestConfig {
  testId: string;
  inputFEN: string;
  lightKing: PieceType;
  darkKing: PieceType;
  lightPlayerId: string;
  darkPlayerId: string;
  timeControl?: "sprint" | "standard" | "marathon" | "off";
  gameMode?: "competitive" | "quick" | "casual";
}

/**
 * Result of executing a move on one engine
 */
export interface MoveExecutionResult {
  success: boolean;
  error?: string;
  beforeFEN: string;
  afterFEN: string;
  beforeSnapshot: EngineStateSnapshot;
  afterSnapshot: EngineStateSnapshot;
}
